<script>
import KanbanColumn from './KanbanColumn.vue';
import KanbanSettingsModal from './KanbanSettingsModal.vue';
import draggable from 'vuedraggable';
import AddLeadModal from './AddLeadModal.vue';
import AddNegotiationModal from './AddNegotiationModal.vue';
import ImportWizardModal from './ImportWizardModal.vue';
import KanbanDrawer from './KanbanDrawer.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useAlert } from 'dashboard/composables';
import CrmReportsAPI from 'dashboard/api/crmReports';
import CrmDealsAPI from 'dashboard/api/crmDeals';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { dealToCard } from './helpers/dealToCard';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'KanbanView',
  components: {
    KanbanColumn,
    KanbanSettingsModal,
    Draggable: draggable,
    AddLeadModal,
    AddNegotiationModal,
    ImportWizardModal,
    KanbanDrawer,
    NextButton,
    DropdownMenu,
  },
  data() {
    return {
      activeInboxId: null,
      activeTeamId: null,
      activeAssigneeId: null,
      // Só no modo deals: lá a origem mora no negócio, e caixa de entrada só
      // existiria para quem tem conversa.
      activeOriginId: null,
      activeStatus: 'open',
      activeDateRange: 'all',
      customDateFrom: '',
      customDateTo: '',
      searchQuery: '',
      showSettingsModal: false,
      showMoreMenu: false,
      isExporting: false,
      showFiltersModal: false,
      showAddLeadModal: false,
      showAddNegotiationModal: false,
      showImportModal: false,
      showAddMenu: false,
      isKanbanLoading: false,
      isLoadingMore: false,
      loadedCount: 0,
      totalExpected: 0,
      drawerConversation: null,
      // Escolha de caixa de entrada ao criar a primeira conversa de um negócio.
      pendingConversation: null,
      pendingInboxId: null,
      isCreatingConversation: false,
      // Selo da carga corrente do quadro conversacional. Ver
      // fetchKanbanConversations: sem ele, duas cargas concorrentes se
      // atropelam e o funil monta pela metade.
      boardRunToken: 0,
      boardRefreshTimer: null,
      searchTimer: null,
    };
  },
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
    currentAccount() {
      return this.$store.getters['accounts/getAccount'](this.currentAccountId);
    },
    isDealsMode() {
      return this.currentAccount?.crm_mode === 'deals';
    },
    // Recorte de criação em segundos unix, espelhando o vocabulário do
    // Crm::PeriodRange do backend ("ontem" tem que significar o mesmo nos dois
    // modos). Nulo = sem filtro. Em modo deals o servidor já corta por
    // created_at; este intervalo alimenta o filtro client-side do modo
    // conversational, cujas conversas chegam pela API de lista, que não
    // aceita recorte de data.
    createdRange() {
      const DAY = 86400;
      const startOfDay = date => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return Math.floor(d.getTime() / 1000);
      };
      const now = Math.floor(Date.now() / 1000);
      const todayStart = startOfDay(new Date());

      switch (this.activeDateRange) {
        case 'today':
          return [todayStart, todayStart + DAY - 1];
        case 'yesterday':
          return [todayStart - DAY, todayStart - 1];
        case '7d':
          return [now - 7 * DAY, now];
        case '30d':
          return [now - 30 * DAY, now];
        case '90d':
          return [now - 90 * DAY, now];
        case 'custom': {
          if (!this.customDateFrom || !this.customDateTo) return null;
          const from = startOfDay(`${this.customDateFrom}T00:00:00`);
          // Dias, não instantes: o último dia entra inteiro.
          const to = startOfDay(`${this.customDateTo}T00:00:00`) + DAY - 1;
          return [from, to];
        }
        default:
          return null;
      }
    },
    dealColumns() {
      return this.$store.getters['crmDeals/getColumns'];
    },
    dealCardsByStage() {
      const deals = this.$store.getters['crmDeals/getDeals'];
      return deals.reduce((acc, deal) => {
        const list = acc[deal.stage_id] || [];
        list.push(dealToCard(deal));
        acc[deal.stage_id] = list;
        return acc;
      }, {});
    },
    uiSettings() {
      return this.$store.getters.getUISettings || {};
    },
    inboxes() {
      return this.$store.getters['inboxes/getInboxes'] || [];
    },
    teams() {
      return this.$store.getters['teams/getTeams'] || [];
    },
    agents() {
      return this.$store.getters['agents/getAgents'] || [];
    },
    origins() {
      return this.$store.getters['crmDeals/getOrigins'] || [];
    },
    // Negócio não tem pendente, adiado nem resolvido: esses são status de
    // conversa, e mandados ao servidor em modo deals zeravam o quadro.
    statusOptions() {
      if (this.isDealsMode) {
        return [
          { value: 'open', label: 'Em andamento' },
          { value: 'won', label: 'Ganho' },
          { value: 'lost', label: 'Perdido' },
          { value: 'all', label: 'Todos' },
        ];
      }
      return [
        { value: 'open', label: 'Abertas' },
        { value: 'pending', label: 'Pendentes' },
        { value: 'snoozed', label: 'Adiadas' },
        { value: 'resolved', label: 'Resolvidas' },
        { value: 'all', label: 'Todas' },
      ];
    },
    funnels() {
      if (this.isDealsMode) {
        // O id vira string porque o resto do módulo compara com o parâmetro da
        // rota, que é sempre string — sem isso, 4 === '4' falha e o funil ativo
        // nunca é encontrado. As colunas vêm do endpoint do quadro, não daqui.
        return this.$store.getters['crmDeals/getFunnels'].map(funnel => ({
          id: String(funnel.id),
          name: funnel.name,
          position: funnel.position,
          columns: [],
        }));
      }
      return this.currentAccount?.custom_attributes?.crm_funnels || [];
    },
    activeFunnelId() {
      return this.$route.params.funnelId || null;
    },
    // A lista de `funnels` acima achata o funil pro formato do quadro e perde
    // as etapas. A importação precisa delas pra oferecer o destino.
    dealFunnelsWithStages() {
      return this.$store.getters['crmDeals/getFunnels'] || [];
    },
    activeFunnel() {
      return this.funnels.find(f => f.id === this.activeFunnelId) || null;
    },
    allLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
    columns() {
      if (this.isDealsMode) {
        return this.dealColumns.map(col => ({
          id: col.stage_id,
          stage_id: col.stage_id,
          name: col.name,
          color: col.color,
          position: col.position,
          stale_after_hours: col.stale_after_hours,
          count: col.count,
          value_sum: col.value_sum,
        }));
      }

      if (!this.activeFunnel) return [];

      const totals = this.$store.getters['crmDeals/getLabelTotals'] || {};

      return this.activeFunnel.columns
        .map(colRef => {
          const colRefNum = Number(colRef);
          const matchById = this.allLabels.find(
            l => l.id === colRefNum || l.id === colRef
          );
          const matchByTitle = !matchById
            ? this.allLabels.find(l => l.title === colRef)
            : null;
          const matchingLabel = matchById || matchByTitle;

          const title = matchingLabel ? matchingLabel.title : colRef;
          const aggregate = totals[title];

          return {
            id: matchingLabel ? matchingLabel.id : colRef,
            name: title,
            label: title,
            color: matchingLabel ? matchingLabel.color : '#ccc',
            // Vêm do servidor (/crm/board/label_totals): a lista de cards é
            // paginada, então somar no cliente subestimava o funil.
            count: aggregate?.count,
            value_sum: aggregate?.value_sum,
          };
        })
        .filter(col => col.name);
    },
    columnLabelsKey() {
      return this.columns
        .map(c => c.label)
        .sort()
        .join('|');
    },
    draggableColumns: {
      get() {
        return [...this.columns];
      },
      set() {
        // Handled by onColumnReorder
      },
    },
    allConversations() {
      return this.$store.getters.getAllConversations || [];
    },
    // Soma do funil inteiro. Os agregados por coluna já vêm do servidor nos
    // dois modos, então basta somá-los; usar as conversas carregadas dava um
    // número menor que o real assim que a lista passava de uma página.
    totalFunnelValue() {
      const fromColumns = this.columns.reduce((acc, column) => {
        const value = parseFloat(column.value_sum);
        return Number.isNaN(value) ? acc : acc + value;
      }, 0);

      if (fromColumns > 0) return fromColumns;

      return this.allConversations.reduce((acc, c) => {
        const val = parseFloat(c.custom_attributes?.deal_value);
        return !Number.isNaN(val) ? acc + val : acc;
      }, 0);
    },
    formattedTotalFunnelValue() {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(this.totalFunnelValue);
    },
    crmNegotiationInboxId() {
      const id =
        this.currentAccount?.custom_attributes?.crm_negotiation_inbox_id;
      return id ? Number(id) : null;
    },
    moreMenuItems() {
      return [
        {
          label: 'Exportar Lista',
          action: 'export-csv',
          value: 'export-csv',
          icon: 'i-lucide-download',
        },
        {
          label: 'Configurações',
          action: 'settings',
          value: 'settings',
          icon: 'i-lucide-settings',
        },
      ];
    },
    addMenuItems() {
      return [
        {
          label: 'Adicionar Lead',
          action: 'add-lead',
          value: 'add-lead',
          icon: 'i-lucide-user-plus',
        },
        {
          label: 'Adicionar Negociação',
          action: 'add-negotiation',
          value: 'add-negotiation',
          icon: 'i-lucide-handshake',
        },
        {
          label: 'Importar planilha',
          action: 'import-csv',
          value: 'import-csv',
          icon: 'i-lucide-upload',
        },
      ];
    },
    activeFiltersCount() {
      let count = 0;
      if (this.activeInboxId !== null) count += 1;
      if (this.activeTeamId !== null) count += 1;
      if (this.activeAssigneeId !== null) count += 1;
      if (this.activeOriginId !== null) count += 1;
      if (this.activeStatus !== 'open') count += 1;
      if (this.activeDateRange !== 'all') count += 1;
      return count;
    },
  },
  watch: {
    '$route.params.funnelId'() {
      this.fetchKanbanConversations();
    },
    activeInboxId() {
      this.fetchKanbanConversations();
    },
    activeTeamId() {
      this.fetchKanbanConversations();
    },
    activeAssigneeId() {
      this.fetchKanbanConversations();
    },
    activeOriginId() {
      this.fetchKanbanConversations();
    },
    // Em modo deals a busca vai ao servidor (o quadro não tem todos os
    // negócios na memória). Espera a pessoa parar de digitar.
    searchQuery() {
      if (!this.isDealsMode) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchDealsBoard(), 300);
    },
    activeStatus() {
      this.fetchKanbanConversations();
    },
    activeDateRange() {
      this.fetchKanbanConversations();
    },
    customDateFrom() {
      if (this.activeDateRange === 'custom') this.fetchKanbanConversations();
    },
    customDateTo() {
      if (this.activeDateRange === 'custom') this.fetchKanbanConversations();
    },
    funnels(newVal) {
      if (newVal.length > 0 && !this.$route.params.funnelId) {
        this.$router.replace({
          name: 'kanban_funnel',
          params: { ...this.$route.params, funnelId: newVal[0].id },
        });
      }
    },
    // F5 (ou link direto) numa URL de funil: `mounted` dispara fetchFunnels
    // sem esperar e já pede o quadro, mas nessa hora `activeFunnel` ainda é
    // nulo e o fetchDealsBoard desiste em silêncio. Quando os funis chegam,
    // ninguém refazia o pedido: o watcher de `columns` só reage a colunas que
    // encheram, e elas nunca enchiam porque o fetch nunca rodou. O funil
    // ficava eternamente em "Este funil não possui colunas". Navegar por
    // dentro do sistema escapava porque aí o watcher da rota refazia a carga.
    activeFunnel(newVal, oldVal) {
      if (newVal && !oldVal) this.fetchKanbanConversations();
    },
    columns(newVal, oldVal) {
      if (newVal.length > 0 && (!oldVal || oldVal.length === 0)) {
        this.fetchKanbanConversations();
      }
    },
    // No F5 as colunas nascem com o id cru da etiqueta ("12") porque a store
    // de etiquetas ainda está vazia; a primeira carga sai com esse id e não
    // acha nada. Quando as etiquetas chegam o nome muda, mas a lista não
    // estava vazia, então o watcher acima não refazia: o quadro ficava em
    // zero. Olha o conjunto de nomes (ordenado, pra arrastar coluna não
    // recarregar).
    columnLabelsKey(newKey, oldKey) {
      if (this.isDealsMode || !oldKey || !newKey) return;
      if (newKey !== oldKey) this.fetchKanbanConversations();
    },
  },
  mounted() {
    const role = this.$store.getters.getCurrentRole;
    this.activeAssigneeId =
      role === 'administrator' ? null : this.$store.getters.getCurrentUserID;

    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('agents/get');

    if (this.isDealsMode) {
      this.$store.dispatch('crmDeals/fetchFunnels');
      this.$store.dispatch('crmDeals/fetchOrigins');
      // Alimenta o indicador de automação no cabeçalho das colunas.
      this.$store.dispatch('crmAutomations/fetch');
      // Definições dos campos personalizados que a gaveta desenha. Sem isto a
      // gaveta só os mostraria pra quem tivesse passado por Configurações
      // antes, porque a lista viria do store já quente.
      this.$store.dispatch('attributes/get');
    }

    // If no funnel selected via route but funnels exist, navigate to first
    if (!this.$route.params.funnelId && this.funnels.length > 0) {
      this.$router.replace({
        name: 'kanban_funnel',
        params: { ...this.$route.params, funnelId: this.funnels[0].id },
      });
    }
    this.fetchKanbanConversations();

    // Deep link vindo da aba de Tarefas: ?dealId=123 abre a gaveta direto.
    if (this.isDealsMode && this.$route.query.dealId) {
      this.openDealFromQuery(this.$route.query.dealId);
    }

    // Tempo real: negócio criado/atualizado/apagado em qualquer lugar da
    // conta reflete aqui (ver actionCable.js -> ActionCableListener no
    // backend). O quadro só recarrega quando o negócio é do funil aberto.
    emitter.on('deal_changed', this.onDealBroadcast);
  },
  beforeUnmount() {
    emitter.off('deal_changed', this.onDealBroadcast);
    clearTimeout(this.boardRefreshTimer);
    clearTimeout(this.searchTimer);
    // Carga em voo não pode escrever no store depois que a tela saiu.
    this.boardRunToken += 1;

    // O quadro em modo conversational enche a MESMA lista do Vuex que a tela de
    // Conversas lê, e com todos os status (inclusive resolvidas). Saindo daqui
    // sem limpar, a caixa de entrada monta exibindo essa sobra por um instante,
    // até o resetAndFetchData dela responder — é a piscada de "apareceram todas
    // as conversas e sumiram". A ChatList sempre refaz a própria busca ao
    // montar, então esvaziar aqui não tira dado de ninguém.
    this.$store.dispatch('emptyAllConversations');
    this.$store.dispatch('clearConversationFilters');
  },
  methods: {
    async fetchKanbanConversations() {
      if (this.isDealsMode) {
        await this.fetchDealsBoard();
        return;
      }

      const labelTitles = this.columns.map(c => c.label);
      if (!labelTitles.length) return;

      // Doze pontos chamam esta função (watchers de filtro, rota, mount, o
      // watcher de `columns`) e ela leva dezenas de requisições sequenciais
      // pra terminar. Sem selo, a carga nova apaga a lista com
      // `emptyAllConversations` no meio da paginação da carga velha, que
      // continua despejando páginas num balde recém-esvaziado: o funil monta
      // com uma fração dos cards e contagens que não fecham. No mount isso
      // acontecia SEMPRE, porque `mounted` chama e o watcher de `columns`
      // chama de novo quando as colunas chegam.
      this.boardRunToken += 1;
      const runToken = this.boardRunToken;
      const isStale = () => runToken !== this.boardRunToken;

      // Agregados por coluna: contagem e soma reais da etiqueta, que a lista
      // paginada de cards não consegue dar.
      this.fetchLabelTotals(labelTitles);

      // Cards vêm de crm/board/cards, não da lista de conversas: mesma
      // visibilidade, mas sem as três contagens por página e sem montar
      // última mensagem/não lidas de cada conversa (umas seis consultas por
      // card que o funil nunca mostra). Com o custo por card lá embaixo, a
      // página cresce para 500 e "Todos" vira um pedido só (status=all no
      // servidor), em vez de uma fila por status.
      const PAGE_SIZE = 500;
      const MAX_PAGES = 20;
      this.isKanbanLoading = true;
      this.isLoadingMore = false;
      this.loadedCount = 0;
      this.totalExpected = 0;

      this.$store.dispatch('emptyAllConversations');

      const filters = {
        assigneeType: 'all',
        status: this.activeStatus,
        sortBy: 'last_activity_at_desc',
        labels: labelTitles,
        inboxId: this.activeInboxId || undefined,
        teamId: this.activeTeamId || undefined,
      };
      // Os eventos em tempo real da store consultam estes filtros para decidir
      // se uma conversa nova entra na lista; ficam iguais aos do quadro.
      this.$store.dispatch('setChatListFilters', filters);

      try {
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await CrmDealsAPI.boardCards({
            labels: labelTitles,
            status: this.activeStatus,
            inbox_id: filters.inboxId,
            team_id: filters.teamId,
            page,
            per_page: PAGE_SIZE,
          });
          // Outra carga assumiu: sai sem mexer em estado nenhum, senão esta
          // aqui zeraria os indicadores de carregamento da carga nova.
          if (isStale()) return;

          const cards = data.payload || [];
          this.$store.commit('SET_ALL_CONVERSATION', cards);
          this.$store.dispatch(
            'conversationLabels/setBulkConversationLabels',
            cards
          );
          this.loadedCount = (
            this.$store.getters.getAllConversations || []
          ).length;
          if (page === 1) this.isKanbanLoading = false;

          if (!data.meta?.has_more) break;
          this.isLoadingMore = true;
        }

        if (isStale()) return;
        this.isLoadingMore = false;
      } catch (error) {
        if (isStale()) return;
        this.isKanbanLoading = false;
        this.isLoadingMore = false;
        // Antes o erro era engolido em silêncio e o board parecia vazio. Agora avisamos.
        // eslint-disable-next-line no-console
        console.error('[Kanban] Falha ao carregar conversas:', error);
        useAlert(
          'Não foi possível carregar os leads do funil. Tente novamente.'
        );
      }
    },
    // Os filtros da tela precisam ir nos DOIS endpoints: o board devolve a
    // contagem e a soma do topo da coluna, a lista devolve os cards. Mandando só
    // num deles, a coluna diria "12 negócios" com 3 cards embaixo.
    dealBoardFilters() {
      const filters = {};
      if (this.activeDateRange !== 'all') {
        filters.period = this.activeDateRange;
        if (this.activeDateRange === 'custom') {
          filters.date_from = this.customDateFrom;
          filters.date_to = this.customDateTo;
        }
      }
      if (this.activeAssigneeId !== null && this.activeAssigneeId > 0) {
        filters.assignee_id = this.activeAssigneeId;
      }
      if (this.activeStatus !== 'all') filters.status = this.activeStatus;

      // No modo conversational estes recortes são feitos no navegador (ver
      // KanbanColumn); no deals só o servidor tem todos os negócios.
      if (this.isDealsMode) {
        // "Não atribuído" é 0 no select; o servidor entende 'none'.
        if (this.activeAssigneeId === 0) filters.assignee_id = 'none';
        if (this.activeTeamId !== null) filters.team_id = this.activeTeamId;
        if (this.activeOriginId !== null) {
          filters.origin_id = this.activeOriginId;
        }
        const query = this.searchQuery.trim();
        if (query) filters.q = query;
      }
      return filters;
    },
    async fetchLabelTotals(labels) {
      try {
        await this.$store.dispatch('crmDeals/fetchLabelTotals', {
          labels,
          ...this.dealBoardFilters(),
          inbox_id: this.activeInboxId || undefined,
          team_id: this.activeTeamId || undefined,
        });
      } catch (error) {
        // Total do topo é informativo: se falhar, o quadro segue com os cards.
        // eslint-disable-next-line no-console
        console.error(
          '[Kanban] Falha ao carregar os totais das colunas:',
          error
        );
      }
    },
    async fetchDealsBoard() {
      const funnelId = this.activeFunnel?.id;
      if (!funnelId) return;

      // Período personalizado incompleto ainda não é um recorte: sem as duas
      // datas o backend ignoraria, e a tela pareceria filtrada sem estar.
      if (
        this.activeDateRange === 'custom' &&
        !(this.customDateFrom && this.customDateTo)
      ) {
        return;
      }

      const filters = this.dealBoardFilters();
      await Promise.all([
        this.$store.dispatch('crmDeals/fetchBoard', {
          funnel_id: funnelId,
          ...filters,
        }),
        this.$store.dispatch('crmDeals/fetchDeals', {
          funnel_id: funnelId,
          per_page: 200,
          ...filters,
        }),
      ]);
    },
    async handleDealMoveFailed({ reason, card } = {}) {
      // Requisito de etapa devolve a lista do que falta; qualquer outra falha
      // cai na mensagem genérica.
      useAlert(
        reason ||
          'Não foi possível mover o negócio para esta etapa. Tente novamente.'
      );
      await this.fetchDealsBoard();

      // Avisar o que falta e deixar a pessoa procurar onde preencher seria
      // meio caminho. A gaveta abre já mostrando os campos exigidos.
      if (reason && card) this.openDrawer(card);
    },
    closeFiltersModal() {
      this.showFiltersModal = false;
    },
    clearFilters() {
      this.activeInboxId = null;
      this.activeTeamId = null;
      this.activeAssigneeId = null;
      this.activeOriginId = null;
      this.activeStatus = 'open';
      this.activeDateRange = 'all';
      this.customDateFrom = '';
      this.customDateTo = '';
    },
    closeSettingsModal() {
      this.showSettingsModal = false;
    },
    async saveFunnel(funnelData) {
      if (this.isDealsMode) {
        const createdFunnel = await this.$store.dispatch(
          'crmDeals/createFunnel',
          { name: funnelData.name, stages: funnelData.stages }
        );
        this.closeSettingsModal();
        // O id precisa virar string aqui pelo mesmo motivo do computed `funnels`:
        // o funil ativo é achado comparando com o parâmetro de rota (sempre string).
        this.$router.push({
          name: 'kanban_funnel',
          params: {
            ...this.$route.params,
            funnelId: String(createdFunnel.id),
          },
        });
        return;
      }

      const allFunnels = [...this.funnels];
      const index = allFunnels.findIndex(f => f.id === funnelData.id);

      if (index >= 0) {
        allFunnels[index] = funnelData;
      } else {
        allFunnels.push(funnelData);
      }

      this.$store.dispatch('accounts/update', {
        custom_attributes: { crm_funnels: allFunnels },
      });
      this.$router.push({
        name: 'kanban_funnel',
        params: { ...this.$route.params, funnelId: funnelData.id },
      });
      this.closeSettingsModal();
    },
    deleteFunnel(id) {
      const allFunnels = this.funnels.filter(f => f.id !== id);

      // Modo `deals`: a exclusão de verdade já aconteceu (KanbanSettingsModal
      // chamou a API e recarregou os funis). Aqui só sobra navegar pra longe
      // do funil apagado, se era o ativo — nada de custom_attributes.
      if (!this.isDealsMode) {
        this.$store.dispatch('accounts/update', {
          custom_attributes: { crm_funnels: allFunnels },
        });
      }

      this.closeSettingsModal();

      if (this.activeFunnelId === id) {
        if (allFunnels.length > 0) {
          this.$router.push({
            name: 'kanban_funnel',
            params: { ...this.$route.params, funnelId: allFunnels[0].id },
          });
        } else {
          this.$router.push({
            name: 'kanban_view',
            params: { accountId: this.$route.params.accountId },
          });
        }
      }
    },
    onColumnReorder(event) {
      if (!this.activeFunnel) return;
      const newColumns = [...this.activeFunnel.columns];
      const [moved] = newColumns.splice(event.oldIndex, 1);
      newColumns.splice(event.newIndex, 0, moved);

      const allFunnels = [...this.funnels];
      const idx = allFunnels.findIndex(f => f.id === this.activeFunnelId);
      if (idx >= 0) {
        allFunnels[idx] = { ...allFunnels[idx], columns: newColumns };
        this.$store.dispatch('accounts/update', {
          custom_attributes: { crm_funnels: allFunnels },
        });
      }
    },
    openDrawer(conversation) {
      this.drawerConversation = conversation;
    },
    async openDealFromQuery(dealId) {
      try {
        const deal = await this.$store.dispatch('crmDeals/fetchDeal', dealId);
        this.openDrawer(dealToCard(deal));
      } catch (error) {
        // Negócio pode ter sido apagado nesse meio tempo; ignora silenciosamente.
      }
    },
    onDealBroadcast(data) {
      if (!this.isDealsMode || !this.activeFunnel) return;

      const belongsHere =
        String(data.funnel_id) === String(this.activeFunnelId);
      // O evento carrega o funil NOVO do negócio. Descartar tudo que não é do
      // funil aberto deixava card fantasma na tela de quem estava olhando o
      // funil de onde ele saiu, até alguém dar refresh. Se ele está no quadro,
      // a mudança interessa mesmo tendo ido embora.
      const isOnBoard = (this.$store.getters['crmDeals/getDeals'] || []).some(
        deal => String(deal.id) === String(data.id)
      );

      if (!belongsHere && !isOnBoard) return;

      this.scheduleBoardRefresh();
    },
    // Cada negócio criado, movido ou apagado disparava uma recarga completa do
    // quadro (duas requisições, uma delas com até 200 negócios). Uma automação
    // mexendo em vários negócios virava uma rajada de recargas concorrentes,
    // que é o que fazia o funil piscar e montar errado. Agrupa a rajada numa
    // recarga só.
    scheduleBoardRefresh() {
      clearTimeout(this.boardRefreshTimer);
      this.boardRefreshTimer = setTimeout(() => {
        this.boardRefreshTimer = null;
        this.fetchDealsBoard();
      }, 600);
    },
    closeDrawer() {
      this.drawerConversation = null;
      try {
        this.$store.dispatch('clearSelectedState');
      } catch (e) {
        // Ignore
      }
    },
    // Negócio excluído pelo drawer: fecha e recarrega o quadro, senão o card
    // continua na coluna até alguém dar refresh.
    async onDealDeleted() {
      this.closeDrawer();
      await this.fetchKanbanConversations();
    },
    handleMoreMenuAction({ action }) {
      this.showMoreMenu = false;
      if (action === 'export-csv') this.exportLeads();
      else if (action === 'settings') this.showSettingsModal = true;
    },
    // Exporta o que está filtrado na tela. Os mesmos parâmetros do quadro vão
    // pro endpoint; em modo conversational vão as etiquetas das colunas, que
    // são o que define o funil ali.
    async exportLeads() {
      this.isExporting = true;
      try {
        const params = { ...this.dealBoardFilters() };
        if (this.isDealsMode) {
          params.funnel_id = this.activeFunnel?.id;
        } else {
          params.funnel_labels = this.columns.map(column => column.label);
        }
        const { data } = await CrmReportsAPI.exportLeads(params);
        const url = window.URL.createObjectURL(
          new Blob([data], { type: 'text/csv' })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `leads-${new Date().toISOString().slice(0, 10)}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        useAlert('Não foi possível exportar os leads. Tente novamente.');
      } finally {
        this.isExporting = false;
      }
    },
    handleAddMenuAction({ action }) {
      this.showAddMenu = false;
      if (action === 'add-lead') this.showAddLeadModal = true;
      else if (action === 'add-negotiation')
        this.showAddNegotiationModal = true;
      else if (action === 'import-csv') this.showImportModal = true;
    },
    // Caixas onde faz sentido abrir a conversa de um negócio, mais provável
    // primeiro. Antes olhava só WhatsApp oficial (Channel::Whatsapp), e quem usa
    // Uazapi tem canal API: a lista vinha vazia e o clique no botão não produzia
    // efeito nenhum. A inbox dedicada de negociações fica de fora de propósito —
    // ela existe pra não receber mensagem.
    inboxesForNewConversation() {
      const peso = inbox => {
        if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return 0;
        if (inbox.channel_type === INBOX_TYPES.API) return 1;
        return 2;
      };
      return (this.$store.getters['inboxes/getInboxes'] || [])
        .filter(inbox => !this.isNegotiationInbox(inbox.id))
        .sort((a, b) => peso(a) - peso(b));
    },
    cancelPendingConversation() {
      this.pendingConversation = null;
      this.pendingInboxId = null;
    },
    async confirmPendingConversation() {
      if (!this.pendingInboxId || !this.pendingConversation) return;
      this.isCreatingConversation = true;
      try {
        const { data } = await ConversationApi.create({
          inbox_id: this.pendingInboxId,
          contact_id: this.pendingConversation.contactId,
        });
        this.cancelPendingConversation();
        // A criação devolve display_id no campo `id` (ver o partial de conversa).
        this.routeToConversation(data.id);
      } catch (e) {
        useAlert('Não foi possível criar a conversa com este contato.');
      } finally {
        this.isCreatingConversation = false;
      }
    },
    isNegotiationInbox(inboxId) {
      return (
        !!this.crmNegotiationInboxId &&
        Number(inboxId) === this.crmNegotiationInboxId
      );
    },
    routeToConversation(id) {
      this.closeDrawer();
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.$route.params.accountId,
          conversation_id: id,
        },
      });
    },
    async navigateToConversation(conversation) {
      // Card de NEGÓCIO (modo deals) e card da inbox dedicada de negociação têm
      // o mesmo destino: a conversa real do contato. No card de negócio o `id` é
      // do negócio, não da conversa (ver helpers/dealToCard.js) — navegar por ele
      // abria conversa errada ou inexistente.
      const isDeal = !!conversation?.is_deal;
      const resolveByContact =
        isDeal || this.isNegotiationInbox(conversation?.inbox_id);

      if (!resolveByContact) {
        this.routeToConversation(conversation?.id ?? conversation);
        return;
      }

      // Conversa que o próprio card aponta. No negócio é o display_id da
      // conversa espelhada — a rota fala display_id, e o conversation_id do
      // negócio é a chave da tabela. No card de negociação é a conversa da
      // inbox do CRM, que serve só de último recurso porque não é onde se fala
      // com o contato.
      const ownConversationId = isDeal
        ? conversation.conversation_display_id
        : conversation.id;
      const contactId = conversation.meta?.sender?.id;
      if (!contactId) {
        if (ownConversationId) this.routeToConversation(ownConversationId);
        return;
      }

      try {
        await this.$store.dispatch('contactConversations/get', contactId);
        const all =
          this.$store.getters['contactConversations/getContactConversation'](
            contactId
          ) || [];
        const candidates = all
          .filter(c => {
            if (this.isNegotiationInbox(c.inbox_id)) return false;
            // A conversa da inbox de negociação é descartada acima; a do negócio
            // é candidata legítima — é a conversa de WhatsApp que originou o card.
            return isDeal || c.id !== conversation.id;
          })
          .sort(
            (a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0)
          );
        // Aberta primeiro; senão a mais recente, que reabre sozinha ao responder.
        // Só cria conversa nova quando o contato não tem nenhuma — criar por cima
        // de uma resolvida encheria a caixa de entrada de duplicatas.
        const main = candidates.find(c => c.status === 'open') || candidates[0];
        if (main) {
          this.routeToConversation(main.id);
          return;
        }
        // Nenhuma conversa existente: em vez de escolher a inbox sozinho, abre a
        // escolha. Criar conversa é ação deliberada e a inbox define por onde o
        // contato passa a ser atendido — quem decide isso é quem opera.
        const usable = this.inboxesForNewConversation();
        if (!usable.length) {
          if (ownConversationId) {
            this.routeToConversation(ownConversationId);
            return;
          }
          useAlert(
            'Nenhuma caixa de entrada disponível para abrir uma conversa com este contato.'
          );
          return;
        }
        this.pendingInboxId = usable[0].id;
        this.pendingConversation = {
          contactId,
          contactName: conversation.meta?.sender?.name || 'este contato',
          inboxes: usable,
        };
      } catch (e) {
        if (ownConversationId) {
          this.routeToConversation(ownConversationId);
          return;
        }
        useAlert('Não foi possível abrir a conversa deste contato.');
      }
    },

    onLeadAdded() {
      // Force fetching the active funnel's conversations to immediately show the new lead
      this.fetchKanbanConversations();
    },
  },
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="flex items-center justify-between px-4 h-14 border-b border-n-weak bg-n-surface-1"
    >
      <div class="flex items-center gap-4">
        <h1
          class="text-heading-2 m-0 flex items-center gap-2 whitespace-nowrap"
        >
          <span class="i-lucide-trello w-6 h-6" />
          CRM / Funil
        </h1>

        <div class="h-6 w-px bg-n-weak mx-1 hidden md:block" />

        <!-- Current Funnel Name + Total Value -->
        <div v-if="activeFunnel" class="flex items-center gap-3 hidden md:flex">
          <span class="text-sm font-semibold text-n-slate-12">
            {{ activeFunnel.name }}
          </span>
          <span
            v-if="totalFunnelValue > 0"
            class="text-xs font-bold text-emerald-400 bg-emerald-900/30 px-2 py-1 rounded-full"
          >
            {{ formattedTotalFunnelValue }}
          </span>
        </div>
        <span v-else class="text-sm text-n-slate-11 italic hidden md:block">
          Selecione um funil na barra lateral
        </span>
      </div>

      <div class="flex items-center gap-3">
        <!-- Search: nome da negociação, contato, telefone ou #ID -->
        <div class="relative flex items-center w-48 lg:w-72">
          <span
            class="i-lucide-search w-4 h-4 shrink-0 text-n-slate-10 absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="search"
            placeholder="Buscar negociação"
            class="no-margin block w-full h-9 m-0 ltr:pl-9 ltr:pr-8 rtl:pr-9 rtl:pl-8 border border-n-weak rounded-xl bg-n-surface-1 text-sm font-medium text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-n-brand focus:border-n-brand"
          />
          <button
            v-if="searchQuery"
            class="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2 flex items-center justify-center p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 border-none bg-transparent cursor-pointer"
            title="Limpar busca"
            @click="searchQuery = ''"
          >
            <span class="i-lucide-x w-3.5 h-3.5 block" />
          </button>
        </div>

        <!-- Add Actions Dropdown (Lead / Negociação / Importar CSV) -->
        <div
          v-if="columns && columns.length > 0"
          v-on-clickaway="() => (showAddMenu = false)"
          class="relative"
        >
          <NextButton
            label="Adicionar"
            icon="i-lucide-plus"
            size="sm"
            @click="showAddMenu = !showAddMenu"
          />
          <DropdownMenu
            v-if="showAddMenu"
            :menu-items="addMenuItems"
            class="ltr:right-0 rtl:left-0 mt-1 w-56 top-full z-50"
            @action="handleAddMenuAction"
          />
        </div>

        <!-- Products Button Removed: Now accessible via Sidebar -->

        <!-- Filters Button -->
        <button
          class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer outline outline-1 border-0"
          :class="
            activeFiltersCount > 0
              ? 'bg-woot-500 text-white hover:bg-woot-600 outline-transparent'
              : 'bg-n-button-color text-n-slate-12 hover:bg-n-alpha-2 outline-n-strong'
          "
          @click="showFiltersModal = true"
        >
          <span class="i-lucide-filter w-4 h-4" />
          Filtros{{ activeFiltersCount > 0 ? ` (${activeFiltersCount})` : '' }}
        </button>

        <!-- Menu de opções: último da barra, encostado no canto -->
        <div v-on-clickaway="() => (showMoreMenu = false)" class="relative">
          <button
            v-tooltip.top="'Mais opções'"
            class="flex items-center justify-center px-3 py-2 rounded-xl text-sm font-semibold bg-n-button-color text-n-slate-12 hover:bg-n-alpha-2 transition-colors outline outline-1 outline-n-strong cursor-pointer border-0"
            @click="showMoreMenu = !showMoreMenu"
          >
            <span
              class="w-4 h-4"
              :class="
                isExporting
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-more-vertical'
              "
            />
          </button>
          <DropdownMenu
            v-if="showMoreMenu"
            :menu-items="moreMenuItems"
            class="ltr:right-0 rtl:left-0 mt-1 w-56 top-full z-50"
            @action="handleMoreMenuAction"
          />
        </div>
      </div>
    </header>

    <!-- Kanban Board -->
    <main
      class="flex-1 min-h-0 overflow-x-auto overflow-y-hidden always-scrollbar"
    >
      <!-- Loading More Indicator (background pagination) -->
      <div v-if="isLoadingMore" class="px-4 pt-2">
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span class="i-lucide-loader-2 w-3 h-3 animate-spin" />
          Carregando mais leads... ({{ loadedCount }} carregados)
        </div>
      </div>

      <div class="flex h-full p-4 gap-4 items-start pb-8">
        <!-- Skeleton Loading -->
        <template v-if="isKanbanLoading && columns.length > 0">
          <div
            v-for="col in columns"
            :key="'skeleton-' + col.id"
            class="flex flex-col w-[320px] flex-shrink-0 max-h-full rounded-xl bg-n-alpha-2 border border-n-weak"
          >
            <div class="p-3 border-b border-n-weak">
              <div class="flex items-center gap-2">
                <span
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: col.color || '#ccc' }"
                />
                <span class="text-sm font-semibold text-n-slate-12">{{
                  col.name
                }}</span>
              </div>
            </div>
            <div class="p-2 space-y-2">
              <div
                v-for="n in 3"
                :key="n"
                class="bg-n-surface-1 border border-n-weak rounded-lg p-3 animate-pulse"
              >
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-full bg-n-slate-4" />
                  <div class="flex-1">
                    <div class="h-3 bg-n-slate-4 rounded w-3/4 mb-1" />
                    <div class="h-2 bg-n-slate-3 rounded w-1/4" />
                  </div>
                </div>
                <div class="h-2 bg-n-slate-3 rounded w-full mb-1" />
                <div class="h-2 bg-n-slate-3 rounded w-2/3" />
              </div>
            </div>
          </div>
        </template>

        <!-- Draggable Columns -->
        <Draggable
          v-if="columns.length > 0 && !isKanbanLoading"
          v-model="draggableColumns"
          group="columns"
          item-key="id"
          handle=".column-drag-handle"
          class="flex gap-4 items-start h-full"
          ghost-class="opacity-50"
          @end="onColumnReorder"
        >
          <template #item="{ element }">
            <KanbanColumn
              :column="element"
              :search-query="isDealsMode ? '' : searchQuery"
              :active-assignee-id="activeAssigneeId"
              :active-inbox-id="activeInboxId"
              :active-team-id="activeTeamId"
              :active-status="activeStatus"
              :created-range="createdRange"
              :all-column-labels="columns.map(c => c.label)"
              :cards="
                isDealsMode ? dealCardsByStage[element.stage_id] || [] : null
              "
              @open-drawer="openDrawer"
              @open-conversation="navigateToConversation"
              @deal-move-failed="handleDealMoveFailed"
            />
          </template>
        </Draggable>

        <!-- Empty Board State: nenhum funil configurado ainda -->
        <div
          v-if="!columns.length && !funnels.length"
          class="flex items-center justify-center w-full h-full text-n-slate-11"
        >
          <div class="text-center max-w-sm">
            <span
              class="i-lucide-layout-dashboard w-12 h-12 mx-auto mb-4 text-n-slate-8 block"
            />
            <h3 class="text-heading-2 mb-2">Nenhum funil configurado</h3>
            <p class="mb-4">
              Crie um funil com as etapas do seu processo de vendas para começar
              a organizar seus negócios.
            </p>
            <NextButton
              label="Criar primeiro funil"
              icon="i-lucide-plus"
              @click="showSettingsModal = true"
            />
          </div>
        </div>

        <!-- Empty Board State: funil existe, mas sem colunas/etapas -->
        <div
          v-else-if="!columns.length"
          class="flex items-center justify-center w-full h-full text-n-slate-11"
        >
          <div class="text-center">
            <span
              class="i-lucide-layout-dashboard w-12 h-12 mx-auto mb-4 text-n-slate-8 block"
            />
            <h3 class="text-heading-2 mb-2">Este funil não possui colunas</h3>
            <p>Edite o funil para adicionar etapas.</p>
          </div>
        </div>
      </div>
    </main>

    <KanbanSettingsModal
      :show="showSettingsModal"
      :funnels="funnels"
      :active-funnel-id="activeFunnelId"
      :is-deals-mode="isDealsMode"
      @close="closeSettingsModal"
      @save-funnel="saveFunnel"
      @delete-funnel="deleteFunnel"
      @select-funnel="activeFunnelId = $event"
    />

    <AddLeadModal
      :show="showAddLeadModal"
      :columns="columns"
      @close="showAddLeadModal = false"
      @lead-added="onLeadAdded"
    />

    <AddNegotiationModal
      :show="showAddNegotiationModal"
      :columns="columns"
      :is-deals-mode="isDealsMode"
      :funnel-id="activeFunnelId"
      @close="showAddNegotiationModal = false"
      @negotiation-added="onLeadAdded"
    />

    <ImportWizardModal
      :show="showImportModal"
      :funnels="dealFunnelsWithStages"
      :active-funnel-id="activeFunnelId"
      @close="showImportModal = false"
      @imported="onLeadAdded"
    />

    <!-- Filters Modal -->
    <woot-modal :show="showFiltersModal" :on-close="closeFiltersModal">
      <div class="flex flex-col h-full bg-white dark:bg-slate-900">
        <div
          class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
        >
          <h2
            class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100"
          >
            Filtros Avançados
          </h2>
        </div>
        <div class="flex-1 px-6 py-6 space-y-4">
          <div v-if="isDealsMode">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Origem</label
            >
            <select
              v-model="activeOriginId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option :value="null">Todas as Origens</option>
              <option
                v-for="origin in origins"
                :key="origin.id"
                :value="origin.id"
              >
                {{ origin.name }}
              </option>
            </select>
          </div>
          <div v-else>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Caixa de Entrada</label
            >
            <select
              v-model="activeInboxId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option :value="null">Todas as Caixas</option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Equipes</label
            >
            <select
              v-model="activeTeamId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option :value="null">Todas as Equipes</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Agentes (Atribuído a)</label
            >
            <select
              v-model="activeAssigneeId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option :value="null">Todos os Agentes</option>
              <option :value="0">Não atribuído</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Status</label
            >
            <select
              v-model="activeStatus"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >Período de criação</label
            >
            <select
              v-model="activeDateRange"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option value="all">Todo o período</option>
              <option value="today">Hoje</option>
              <option value="yesterday">Ontem</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="custom">Personalizado</option>
            </select>

            <!-- As duas datas juntas formam o recorte: com uma só, o quadro
                 continua mostrando tudo, então nem busca. -->
            <div v-if="activeDateRange === 'custom'" class="flex gap-2 mt-2">
              <input
                v-model="customDateFrom"
                type="date"
                class="flex-1 px-2 py-1.5 border border-n-weak rounded-md bg-n-surface-1 text-sm"
              />
              <input
                v-model="customDateTo"
                type="date"
                class="flex-1 px-2 py-1.5 border border-n-weak rounded-md bg-n-surface-1 text-sm"
              />
            </div>
          </div>
        </div>
        <div
          class="px-6 py-4 border-t border-n-weak flex items-center justify-between bg-n-alpha-1"
        >
          <NextButton
            v-if="activeFiltersCount > 0"
            label="Limpar Filtros"
            variant="ghost"
            color="ruby"
            @click="clearFilters"
          />
          <div v-else />
          <NextButton label="Aplicar" @click="closeFiltersModal" />
        </div>
      </div>
    </woot-modal>

    <!-- Drawer do lead (extraido) -->
    <KanbanDrawer
      v-if="drawerConversation"
      :conversation="drawerConversation"
      @close="closeDrawer"
      @deleted="onDealDeleted"
    />

    <!-- Escolha da caixa de entrada ao abrir a primeira conversa de um negócio.
         Só aparece quando o contato ainda não tem conversa: havendo uma, ela é
         aberta direto, sem perguntar nada. -->
    <woot-modal
      :show="!!pendingConversation"
      :on-close="cancelPendingConversation"
    >
      <div v-if="pendingConversation" class="flex flex-col">
        <div class="px-6 py-4 border-b border-n-weak">
          <h2 class="text-lg font-semibold m-0 text-n-slate-12">
            Abrir conversa
          </h2>
        </div>
        <div class="px-6 py-6 space-y-4">
          <p class="text-sm text-n-slate-11 m-0">
            {{ pendingConversation.contactName }} ainda não tem conversa.
            Escolha por onde falar com este contato.
          </p>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1"
              >Caixa de Entrada</label
            >
            <select
              v-model="pendingInboxId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm font-medium focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option
                v-for="inbox in pendingConversation.inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-n-weak flex justify-end gap-2">
          <NextButton
            label="Cancelar"
            variant="clear"
            @click="cancelPendingConversation"
          />
          <NextButton
            label="Abrir conversa"
            :is-loading="isCreatingConversation"
            @click="confirmPendingConversation"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
